import { IActiveId } from "@/lib/widgetsLayoutDir";
import { ErrorInfo } from "react";
import axios from "axios";
import { getAudience, getConfig } from "@/components/config";

// Log widget render crashes (caught by WidgetErrorBoundary) to the backend
// via POST /Logs (LQC-1886, LQC-1841).

// One log per widget+message per session - a widget that crashes on every
// render, or a user retrying into the same crash, must not spam the endpoint.
const loggedErrorKeys = new Set<string>();

// Stringified into the outgoing `message` so fields can be parsed back out.
export interface WidgetErrorDetails {
  source: string; // Widget name.
  errorMessage: string;
  stack: string;
  componentStack: string;
  activeIdValue: string; // ESID or block id in flight when the crash happened ("" when no drag).
  activeDragType: string; // DRAG_HEADER type saying which of the two activeIdValue is.
}

export function buildWidgetErrorPayload(
  widgetName: string,
  error: Error,
  info: ErrorInfo,
  activeId?: IActiveId,
): WidgetErrorDetails {
  return {
    source: widgetName,
    errorMessage: error.message,
    stack: error.stack ?? "",
    componentStack: info.componentStack ?? "",
    activeIdValue: activeId?.value ?? "",
    activeDragType: activeId?.headerDragging ?? "",
  };
}

export function shouldLogWidgetError(
  widgetName: string,
  error: Error,
): boolean {
  const key = `${widgetName}|${error.message}`;
  if (loggedErrorKeys.has(key)) return false;
  loggedErrorKeys.add(key);
  return true;
}

export function clearLoggedWidgetErrors() {
  loggedErrorKeys.clear();
}

// Error = nothing handled it (crash). Warning = caught and the app carried on.
export type ClientLogLevel = "Trace" | "Information" | "Warning" | "Error";

// Deliberately NOT fetchRequestWrapper: its catch block toasts and can
// trigger an action-summary refresh - a failed telemetry call must never
// surface a second error to the user.
const postClientLog = async (
  details: WidgetErrorDetails,
  getAccessTokenSilently: any,
  level: ClientLogLevel,
) => {
  const payload = { level, message: JSON.stringify(details) };
  const token = await getAccessTokenSilently({
    authorizationParams: {
      audience: `${getAudience()?.audience}`,
      scope: "openid profile email",
    },
  });
  await axios({
    method: "POST",
    url: `${getConfig().baseUrl}/Logs`,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify(payload),
  });
};

export const logWidgetError = async (
  widgetName: string,
  error: Error,
  info: ErrorInfo,
  getAccessTokenSilently: any,
  activeId?: IActiveId,
) => {
  try {
    if (!shouldLogWidgetError(widgetName, error)) return;

    const details = buildWidgetErrorPayload(widgetName, error, info, activeId);
    await postClientLog(details, getAccessTokenSilently, "Error");
  } catch {
    // Swallow - never let error logging crash the app.
  }
};

// The root ErrorBoundary wraps Auth0Provider, so it cannot use useAuth0.
// ErrorLogTokenRegistrar (mounted inside the provider) registers the token
// accessor here for logAppRootError to use.
let registeredGetAccessTokenSilently: any = null;

export function registerErrorLogTokenAccessor(getAccessTokenSilently: any) {
  registeredGetAccessTokenSilently = getAccessTokenSilently;
}

export const logAppRootError = async (error: Error, info: ErrorInfo) => {
  try {
    // A crash before Auth0 mounts has no token, so it cannot be logged.
    if (!registeredGetAccessTokenSilently) return;
    if (!shouldLogWidgetError("AppRoot", error)) return;

    const details = buildWidgetErrorPayload("AppRoot", error, info);
    await postClientLog(details, registeredGetAccessTokenSilently, "Error");
  } catch {
    // Swallow - never let error logging crash the app.
  }
};

// General-purpose entry point for catch blocks. Fire-and-forget, never
// throws. Pass getAccessTokenSilently if it is in scope, otherwise the
// accessor registered by ErrorLogTokenRegistrar is used. Posts as Warning
// (a handled failure); pass level to override.
export const logClientError = async (
  source: string,
  error: unknown,
  getAccessTokenSilently?: any,
  level: ClientLogLevel = "Warning",
) => {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    const accessor = getAccessTokenSilently ?? registeredGetAccessTokenSilently;
    if (!accessor) return;
    if (!shouldLogWidgetError(source, err)) return;

    const details: WidgetErrorDetails = {
      source,
      errorMessage: err.message,
      stack: err.stack ?? "",
      componentStack: "",
      activeIdValue: "",
      activeDragType: "",
    };
    await postClientLog(details, accessor, level);
  } catch {
    // Swallow - never let error logging crash the app.
  }
};
